import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import axios from 'axios';
import {
    TrendingUp,
    IndianRupee,
    ShoppingBag,
    Package,
    Loader2,
    ArrowUpRight,
    ArrowDownLeft,
    BarChart3,
    Wallet,
    Clock,
    CheckCircle,
    XCircle,
    DollarSign,
    Heart,
    Send,
    AlertCircle,
    Copy,
    ExternalLink,
    History,
    CreditCard,
    X,
    ChevronDown,
    ChevronUp,
    Banknote,
    Globe,
    RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const Earnings = () => {
    const { user, token, API } = useContext(AuthContext);
    const [loading, setLoading] = useState(true);
    const [earningsEnabled, setEarningsEnabled] = useState(false);

    // Wallet state
    const [wallet, setWallet] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [withdrawals, setWithdrawals] = useState([]);
    const [merchEarnings, setMerchEarnings] = useState(null);

    // Payout info form
    const [upiId, setUpiId] = useState('');
    const [paypalMe, setPaypalMe] = useState('');
    const [savingPayout, setSavingPayout] = useState(false);

    // Withdrawal modal
    const [showWithdrawModal, setShowWithdrawModal] = useState(false);
    const [withdrawAmount, setWithdrawAmount] = useState('');
    const [withdrawMethod, setWithdrawMethod] = useState('upi');
    const [withdrawing, setWithdrawing] = useState(false);

    // Section toggles
    const [showTransactions, setShowTransactions] = useState(false);
    const [showProducts, setShowProducts] = useState(false);

    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        fetchAllData();
    }, [API, token]); // eslint-disable-line

    const fetchAllData = async () => {
        try {
            const settingsRes = await axios.get(`${BACKEND_URL}/api/settings/features`);
            const isEnabled = settingsRes.data.earnings_enabled || false;
            setEarningsEnabled(isEnabled);
            if (!isEnabled) { setLoading(false); return; }

            const [walletRes, txRes, wdRes, merchRes] = await Promise.all([
                axios.get(`${API}/wallet/me`, { headers }),
                axios.get(`${API}/wallet/transactions`, { headers }),
                axios.get(`${API}/wallet/withdrawals`, { headers }),
                axios.get(`${API}/earnings/me`, { headers }).catch(() => ({ data: null })),
            ]);

            setWallet(walletRes.data);
            setTransactions(txRes.data.transactions || []);
            setWithdrawals(wdRes.data.withdrawals || []);
            setMerchEarnings(merchRes.data);

            // Pre-fill payout info
            const pi = walletRes.data.payout_info || {};
            setUpiId(pi.upi_id || '');
            setPaypalMe(pi.paypal_me || '');
        } catch (e) {
            console.error('Fetch error:', e);
            toast.error('Failed to load wallet data');
        } finally {
            setLoading(false);
        }
    };

    const handleSavePayout = async () => {
        setSavingPayout(true);
        try {
            await axios.put(`${API}/wallet/payout-info`, {
                upi_id: upiId,
                paypal_me: paypalMe
            }, { headers });
            toast.success('Payout info saved!');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to save');
        } finally {
            setSavingPayout(false);
        }
    };

    const handleWithdraw = async () => {
        if (!withdrawAmount || parseFloat(withdrawAmount) < 100) {
            toast.error('Minimum withdrawal is ₹100');
            return;
        }
        setWithdrawing(true);
        try {
            await axios.post(`${API}/wallet/withdraw`, {
                amount: parseFloat(withdrawAmount),
                method: withdrawMethod
            }, { headers });
            toast.success('Withdrawal requested! You\'ll be paid within T+2 business days.');
            setShowWithdrawModal(false);
            setWithdrawAmount('');
            fetchAllData(); // Refresh
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Withdrawal failed');
        } finally {
            setWithdrawing(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen relative overflow-hidden hero-background flex items-center justify-center">
                <div className="text-center">
                    <Loader2 className="w-16 h-16 text-purple-400 animate-spin mx-auto mb-4" />
                    <p className="text-gray-400 text-lg">Loading wallet...</p>
                </div>
            </div>
        );
    }

    if (!earningsEnabled) {
        return (
            <div className="min-h-screen relative overflow-hidden hero-background flex items-center justify-center">
                <div className="text-center max-w-md">
                    <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-amber-500/20 to-orange-500/20 rounded-3xl flex items-center justify-center">
                        <Wallet className="w-12 h-12 text-amber-400" />
                    </div>
                    <h2 className="text-3xl font-bold text-white mb-3">Earnings Coming Soon</h2>
                    <p className="text-gray-400 text-lg">The earnings tracking feature is not yet available. Check back soon!</p>
                </div>
            </div>
        );
    }

    const w = wallet || {};
    const balance = w.balance || 0;
    const available = balance - (w.pending_withdrawal || 0);
    const tipRev = w.tip_revenue || 0;
    const merchRev = w.merch_revenue || 0;
    const refundRev = w.refund_revenue || 0;
    const totalEarned = w.total_earned || 0;
    const totalWithdrawn = w.total_withdrawn || 0;
    const hasPendingWd = (w.pending_withdrawal_count || 0) > 0;

    const merchData = merchEarnings || { products: [], daily_chart: [], total_sales: 0 };

    const txnTypeLabel = (t) => {
        switch (t) {
            case 'tip_credit': return { label: 'Tip Received', icon: Heart, color: 'text-pink-400', bg: 'bg-pink-500/20' };
            case 'merch_credit': return { label: 'Merch Royalty', icon: ShoppingBag, color: 'text-green-400', bg: 'bg-green-500/20' };
            case 'order_refund': return { label: 'Order Refund', icon: RotateCcw, color: 'text-cyan-400', bg: 'bg-cyan-500/20' };
            case 'withdrawal_request': return { label: 'Withdrawal', icon: Send, color: 'text-orange-400', bg: 'bg-orange-500/20' };
            case 'withdrawal_settled': return { label: 'Paid Out', icon: CheckCircle, color: 'text-emerald-400', bg: 'bg-emerald-500/20' };
            case 'withdrawal_rejected': return { label: 'Refunded', icon: XCircle, color: 'text-red-400', bg: 'bg-red-500/20' };
            default: return { label: t, icon: CreditCard, color: 'text-gray-400', bg: 'bg-gray-500/20' };
        }
    };

    const wdStatusBadge = (status) => {
        const map = {
            pending: { color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', label: 'Pending Review' },
            approved: { color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', label: 'Approved' },
            processing: { color: 'bg-purple-500/20 text-purple-400 border-purple-500/30', label: 'Processing' },
            settled: { color: 'bg-green-500/20 text-green-400 border-green-500/30', label: 'Settled ✓' },
            rejected: { color: 'bg-red-500/20 text-red-400 border-red-500/30', label: 'Rejected' },
        };
        const s = map[status] || map.pending;
        return <span className={`text-xs px-2 py-1 rounded-full border ${s.color}`}>{s.label}</span>;
    };

    return (
        <div className="min-h-screen relative overflow-hidden hero-background">
            <div className="max-w-7xl mx-auto px-4 py-24 relative z-10">
                {/* Header */}
                <div className="mb-10 animate-fadeIn">
                    <h1 className="text-5xl font-bold text-white mb-2">Wallet & Earnings</h1>
                    <p className="text-gray-400 text-lg">Your complete revenue dashboard — tips, merch royalties, and payouts.</p>
                </div>

                {/* ══════════ WALLET BALANCE HERO ══════════ */}
                <div className="glass-card rounded-3xl neon-border mb-8 overflow-hidden animate-fadeIn">
                    <div className="bg-gradient-to-r from-purple-900/40 via-indigo-900/30 to-cyan-900/40 p-8">
                        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
                            <div>
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl flex items-center justify-center shadow-lg shadow-amber-500/30">
                                        <Wallet className="w-6 h-6 text-white" />
                                    </div>
                                    <span className="text-gray-400 text-sm font-medium uppercase tracking-wider">Wallet Balance</span>
                                </div>
                                <p className="text-6xl font-bold text-white mb-1">
                                    ₹{balance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                </p>
                                <p className="text-gray-400 text-sm">
                                    Available: <span className="text-green-400 font-medium">₹{available.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                                    {w.pending_withdrawal > 0 && (
                                        <span className="text-yellow-400 ml-3">
                                            (₹{(w.pending_withdrawal || 0).toLocaleString()} pending)
                                        </span>
                                    )}
                                </p>
                            </div>

                            <button
                                onClick={() => {
                                    setWithdrawAmount(available > 0 ? available.toFixed(2) : '');
                                    setShowWithdrawModal(true);
                                }}
                                disabled={available < 100 || hasPendingWd}
                                className={`px-8 py-4 rounded-2xl font-bold text-lg flex items-center gap-3 transition-all duration-300 ${available >= 100 && !hasPendingWd
                                    ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:shadow-lg hover:shadow-green-500/30 hover:scale-105'
                                    : 'bg-gray-700 text-gray-400 cursor-not-allowed'
                                    }`}
                            >
                                <Banknote className="w-6 h-6" />
                                {hasPendingWd ? 'Withdrawal Pending' : 'Withdraw'}
                            </button>
                        </div>
                    </div>
                </div>

                {/* ══════════ REVENUE BREAKDOWN ══════════ */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
                    {/* Tip Revenue */}
                    <div className="glass-card p-6 rounded-2xl neon-border animate-fadeIn hover:scale-[1.03] transition-transform">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-gray-400 text-sm font-medium mb-1">Tip Revenue</p>
                                <p className="text-3xl font-bold text-pink-400">₹{tipRev.toLocaleString('en-IN')}</p>
                                <p className="text-gray-500 text-xs mt-1">90% of tips received</p>
                            </div>
                            <div className="w-14 h-14 bg-gradient-to-br from-pink-500 to-rose-600 rounded-2xl flex items-center justify-center shadow-lg shadow-pink-500/30">
                                <Heart className="w-7 h-7 text-white" />
                            </div>
                        </div>
                    </div>

                    {/* Merch Revenue */}
                    <div className="glass-card p-6 rounded-2xl neon-border animate-fadeIn hover:scale-[1.03] transition-transform" style={{ animationDelay: '0.1s' }}>
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-gray-400 text-sm font-medium mb-1">Merch Royalty</p>
                                <p className="text-3xl font-bold text-green-400">₹{merchRev.toLocaleString('en-IN')}</p>
                                <p className="text-gray-500 text-xs mt-1">80% of profit margin</p>
                            </div>
                            <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg shadow-green-500/30">
                                <ShoppingBag className="w-7 h-7 text-white" />
                            </div>
                        </div>
                    </div>

                    {/* Order Refunds */}
                    <div className="glass-card p-6 rounded-2xl neon-border animate-fadeIn hover:scale-[1.03] transition-transform" style={{ animationDelay: '0.15s' }}>
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-gray-400 text-sm font-medium mb-1">Order Refunds</p>
                                <p className="text-3xl font-bold text-cyan-400">₹{refundRev.toLocaleString('en-IN')}</p>
                                <p className="text-gray-500 text-xs mt-1">Canceled order refunds</p>
                            </div>
                            <div className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg shadow-cyan-500/30">
                                <RotateCcw className="w-7 h-7 text-white" />
                            </div>
                        </div>
                    </div>

                    {/* Total Earned */}
                    <div className="glass-card p-6 rounded-2xl neon-border animate-fadeIn hover:scale-[1.03] transition-transform" style={{ animationDelay: '0.2s' }}>
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-gray-400 text-sm font-medium mb-1">Total Earned</p>
                                <p className="text-3xl font-bold text-amber-400">₹{totalEarned.toLocaleString('en-IN')}</p>
                                <p className="text-gray-500 text-xs mt-1">Lifetime earnings</p>
                            </div>
                            <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center shadow-lg shadow-amber-500/30">
                                <TrendingUp className="w-7 h-7 text-white" />
                            </div>
                        </div>
                    </div>

                    {/* Total Withdrawn */}
                    <div className="glass-card p-6 rounded-2xl neon-border animate-fadeIn hover:scale-[1.03] transition-transform" style={{ animationDelay: '0.3s' }}>
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-gray-400 text-sm font-medium mb-1">Withdrawn</p>
                                <p className="text-3xl font-bold text-purple-400">₹{totalWithdrawn.toLocaleString('en-IN')}</p>
                                <p className="text-gray-500 text-xs mt-1">Paid out to you</p>
                            </div>
                            <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-violet-600 rounded-2xl flex items-center justify-center shadow-lg shadow-purple-500/30">
                                <Banknote className="w-7 h-7 text-white" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* ══════════ PAYOUT INFO + WITHDRAWAL HISTORY ══════════ */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                    {/* Payout Settings */}
                    <div className="glass-card p-8 rounded-2xl neon-border animate-fadeIn">
                        <h2 className="text-xl font-bold text-white flex items-center mb-6">
                            <CreditCard className="w-5 h-5 mr-3 text-cyan-400" />
                            Payout Settings
                        </h2>

                        <div className="space-y-5">
                            <div>
                                <label className="text-gray-400 text-sm font-medium mb-2 block">UPI ID</label>
                                <div className="flex gap-2">
                                    <div className="relative flex-1">
                                        <IndianRupee className="absolute left-3 top-3.5 w-4 h-4 text-gray-500" />
                                        <input
                                            type="text"
                                            value={upiId}
                                            onChange={e => setUpiId(e.target.value)}
                                            placeholder="yourname@upi"
                                            className="w-full bg-gray-800/60 border border-gray-700 rounded-xl px-10 py-3 text-white text-sm
                                                       focus:border-purple-500 focus:ring-1 focus:ring-purple-500/30 outline-none transition-colors"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label className="text-gray-400 text-sm font-medium mb-2 block">PayPal.me Link</label>
                                <div className="relative">
                                    <Globe className="absolute left-3 top-3.5 w-4 h-4 text-gray-500" />
                                    <input
                                        type="text"
                                        value={paypalMe}
                                        onChange={e => setPaypalMe(e.target.value)}
                                        placeholder="paypal.me/yourname"
                                        className="w-full bg-gray-800/60 border border-gray-700 rounded-xl px-10 py-3 text-white text-sm
                                                   focus:border-purple-500 focus:ring-1 focus:ring-purple-500/30 outline-none transition-colors"
                                    />
                                </div>
                            </div>

                            <button
                                onClick={handleSavePayout}
                                disabled={savingPayout || (!upiId && !paypalMe)}
                                className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold py-3 rounded-xl
                                           hover:shadow-lg hover:shadow-cyan-500/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
                            >
                                {savingPayout ? 'Saving...' : 'Save Payout Info'}
                            </button>

                            <p className="text-gray-500 text-xs text-center">
                                Settlement within T+2 business days after admin approval.
                            </p>
                        </div>
                    </div>

                    {/* Withdrawal History */}
                    <div className="glass-card p-8 rounded-2xl neon-border animate-fadeIn" style={{ animationDelay: '0.1s' }}>
                        <h2 className="text-xl font-bold text-white flex items-center mb-6">
                            <History className="w-5 h-5 mr-3 text-purple-400" />
                            Withdrawal History
                        </h2>

                        {withdrawals.length > 0 ? (
                            <div className="space-y-3 max-h-80 overflow-y-auto pr-2" style={{ scrollbarWidth: 'thin' }}>
                                {withdrawals.map((wd, i) => (
                                    <div key={wd.id || i} className="bg-gray-800/50 rounded-xl p-4 border border-gray-700/50">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-white font-bold text-lg">₹{wd.amount?.toLocaleString('en-IN')}</span>
                                            {wdStatusBadge(wd.status)}
                                        </div>
                                        <div className="flex items-center justify-between text-xs text-gray-500">
                                            <span className="uppercase">{wd.payout_method} → {wd.payout_address}</span>
                                            <span>{wd.requested_at ? new Date(wd.requested_at).toLocaleDateString() : ''}</span>
                                        </div>
                                        {wd.admin_notes && (
                                            <p className="text-xs text-gray-400 mt-2 italic">Note: {wd.admin_notes}</p>
                                        )}
                                        {wd.payment_reference && (
                                            <p className="text-xs text-green-400/70 mt-1">Ref: {wd.payment_reference}</p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-12 text-gray-500">
                                <Banknote className="w-12 h-12 mx-auto mb-3 opacity-30" />
                                <p>No withdrawals yet</p>
                                <p className="text-xs mt-1">Request one when your balance is ₹100+</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* ══════════ TRANSACTION HISTORY (Collapsible) ══════════ */}
                <div className="glass-card rounded-2xl neon-border mb-8 animate-fadeIn" style={{ animationDelay: '0.2s' }}>
                    <button
                        onClick={() => setShowTransactions(!showTransactions)}
                        className="w-full p-6 flex items-center justify-between text-left hover:bg-white/5 transition-colors rounded-2xl"
                    >
                        <h2 className="text-xl font-bold text-white flex items-center">
                            <History className="w-5 h-5 mr-3 text-blue-400" />
                            Transaction History
                            <span className="ml-3 text-sm font-normal text-gray-500">({transactions.length})</span>
                        </h2>
                        {showTransactions ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                    </button>

                    {showTransactions && (
                        <div className="px-6 pb-6">
                            {transactions.length > 0 ? (
                                <div className="space-y-2 max-h-96 overflow-y-auto pr-2" style={{ scrollbarWidth: 'thin' }}>
                                    {transactions.map((tx, i) => {
                                        const info = txnTypeLabel(tx.type);
                                        const Icon = info.icon;
                                        const isPositive = tx.amount > 0;
                                        return (
                                            <div key={tx.id || i} className="flex items-center justify-between bg-gray-800/30 rounded-xl px-4 py-3 border border-gray-700/30 hover:bg-gray-800/50 transition-colors">
                                                <div className="flex items-center gap-3">
                                                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${info.bg}`}>
                                                        <Icon className={`w-5 h-5 ${info.color}`} />
                                                    </div>
                                                    <div>
                                                        <p className="text-white text-sm font-medium">{info.label}</p>
                                                        <p className="text-gray-500 text-xs">{tx.description}</p>
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <p className={`font-bold text-sm ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
                                                        {isPositive ? '+' : ''}₹{Math.abs(tx.amount).toLocaleString('en-IN')}
                                                    </p>
                                                    <p className="text-gray-500 text-xs">
                                                        {tx.created_at ? new Date(tx.created_at).toLocaleDateString() : ''}
                                                    </p>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="text-center py-8 text-gray-500">
                                    <p>No transactions yet. Earn tips or sell merch to get started!</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* ══════════ PRODUCT PERFORMANCE (Collapsible) ══════════ */}
                <div className="glass-card rounded-2xl neon-border animate-fadeIn" style={{ animationDelay: '0.3s' }}>
                    <button
                        onClick={() => setShowProducts(!showProducts)}
                        className="w-full p-6 flex items-center justify-between text-left hover:bg-white/5 transition-colors rounded-2xl"
                    >
                        <h2 className="text-xl font-bold text-white flex items-center">
                            <Package className="w-5 h-5 mr-3 text-amber-400" />
                            Product Performance
                            <span className="ml-3 text-sm font-normal text-gray-500">({merchData.products?.length || 0} products)</span>
                        </h2>
                        {showProducts ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                    </button>

                    {showProducts && (
                        <div className="px-6 pb-6">
                            {merchData.products?.length > 0 ? (
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead>
                                            <tr className="border-b border-gray-700 text-left">
                                                <th className="pb-3 text-gray-400 font-medium text-sm">Product</th>
                                                <th className="pb-3 text-gray-400 font-medium text-sm text-center">Category</th>
                                                <th className="pb-3 text-gray-400 font-medium text-sm text-right">Price</th>
                                                <th className="pb-3 text-gray-400 font-medium text-sm text-right">Sales</th>
                                                <th className="pb-3 text-gray-400 font-medium text-sm text-right">Your Earnings</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {merchData.products.map(product => (
                                                <tr key={product.id} className="border-b border-gray-800 hover:bg-gray-800/40 transition-colors">
                                                    <td className="py-4">
                                                        <div className="flex items-center gap-3">
                                                            {product.image_url ? (
                                                                <img src={product.image_url} alt={product.name} className="w-10 h-10 rounded-lg object-cover" />
                                                            ) : (
                                                                <div className="w-10 h-10 bg-gray-700 rounded-lg flex items-center justify-center">
                                                                    <Package className="w-5 h-5 text-gray-500" />
                                                                </div>
                                                            )}
                                                            <span className="text-white font-medium text-sm">{product.name}</span>
                                                        </div>
                                                    </td>
                                                    <td className="py-4 text-center">
                                                        <span className="text-gray-400 text-sm capitalize bg-gray-800 px-2 py-0.5 rounded">{product.category}</span>
                                                    </td>
                                                    <td className="py-4 text-right text-gray-300 text-sm font-mono">₹{product.price}</td>
                                                    <td className="py-4 text-right text-white font-semibold text-sm">{product.sales_count}</td>
                                                    <td className="py-4 text-right text-amber-400 font-bold text-sm font-mono">₹{product.your_earnings}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="text-center py-8 text-gray-500">
                                    <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
                                    <p>No products yet. Submit merch from your dashboard!</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* ══════════ WITHDRAWAL MODAL ══════════ */}
            {showWithdrawModal && (
                <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                    onClick={() => setShowWithdrawModal(false)}>
                    <div className="glass-card rounded-3xl p-8 w-full max-w-md border border-purple-500/30"
                        onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-2xl font-bold text-white">Withdraw Funds</h3>
                            <button onClick={() => setShowWithdrawModal(false)} className="text-gray-400 hover:text-white">
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        {/* Available balance */}
                        <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 rounded-xl p-4 mb-6 border border-green-500/20">
                            <p className="text-gray-400 text-sm">Available Balance</p>
                            <p className="text-3xl font-bold text-green-400">₹{available.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
                        </div>

                        {/* Amount */}
                        <div className="mb-5">
                            <label className="text-gray-400 text-sm font-medium mb-2 block">Amount (₹)</label>
                            <input
                                type="number"
                                value={withdrawAmount}
                                onChange={e => setWithdrawAmount(e.target.value)}
                                min="100"
                                max={available}
                                placeholder="Min ₹100"
                                className="w-full bg-gray-800/60 border border-gray-700 rounded-xl px-4 py-3 text-white text-lg font-mono
                                           focus:border-green-500 focus:ring-1 focus:ring-green-500/30 outline-none transition-colors"
                            />
                        </div>

                        {/* Method */}
                        <div className="mb-6">
                            <label className="text-gray-400 text-sm font-medium mb-2 block">Payout Method</label>
                            <div className="grid grid-cols-2 gap-3">
                                <button
                                    onClick={() => setWithdrawMethod('upi')}
                                    className={`p-4 rounded-xl border-2 text-center transition-all ${withdrawMethod === 'upi'
                                        ? 'border-green-500 bg-green-500/10 text-green-400'
                                        : 'border-gray-700 bg-gray-800/50 text-gray-400 hover:border-gray-600'
                                        }`}
                                >
                                    <IndianRupee className="w-6 h-6 mx-auto mb-1" />
                                    <span className="text-sm font-medium">UPI</span>
                                    {upiId && <p className="text-xs mt-1 truncate opacity-70">{upiId}</p>}
                                </button>
                                <button
                                    onClick={() => setWithdrawMethod('paypal')}
                                    className={`p-4 rounded-xl border-2 text-center transition-all ${withdrawMethod === 'paypal'
                                        ? 'border-blue-500 bg-blue-500/10 text-blue-400'
                                        : 'border-gray-700 bg-gray-800/50 text-gray-400 hover:border-gray-600'
                                        }`}
                                >
                                    <Globe className="w-6 h-6 mx-auto mb-1" />
                                    <span className="text-sm font-medium">PayPal</span>
                                    {paypalMe && <p className="text-xs mt-1 truncate opacity-70">{paypalMe}</p>}
                                </button>
                            </div>
                        </div>

                        {/* T+2 Notice */}
                        <div className="flex items-start gap-3 mb-6 p-3 bg-yellow-500/10 rounded-xl border border-yellow-500/20">
                            <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                            <p className="text-yellow-400/80 text-xs">
                                Withdrawals are settled within <strong>T+2 business days</strong> after admin review. You'll receive funds to your {withdrawMethod === 'upi' ? 'UPI' : 'PayPal'} account.
                            </p>
                        </div>

                        <button
                            onClick={handleWithdraw}
                            disabled={withdrawing || !withdrawAmount || parseFloat(withdrawAmount) < 100}
                            className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white font-bold py-4 rounded-xl
                                       hover:shadow-lg hover:shadow-green-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 text-lg"
                        >
                            {withdrawing ? (
                                <span className="flex items-center justify-center gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Processing...</span>
                            ) : (
                                `Withdraw ₹${withdrawAmount || '0'}`
                            )}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Earnings;
